import { useState } from 'react';
import { Icon } from '../../../components/ui/Icon';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { canWriteAdmin } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import type { TassaLite } from '../fisco/fiscoApi';

/**
 * I comandi su una tassa, portati da Fisco nella bacheca e nel calendario
 * (16/10/2026, Lorenzo: togliere Fisco e Situazione dal menu senza perdere niente).
 * Stessi pezzi di Fisco, nessuna logica nuova:
 *   Segna pagata                          → SettlePopover + pay (source TAX), come RigaTassa
 *   Inserisci la cifra del commercialista → modale newTax con intent "cifra"
 *   Modifica la tassa / Elimina la tassa  → modale newTax / DELETE /admin/taxes/:id
 * Le spunte delle rate pagate di solito arrivano da sole dalla banca: "Segna
 * pagata" serve per i pagamenti che la banca non riconosce.
 */
export function AzioniTassa({ tassa, rata, nome, aperta, compatta }: {
  tassa: TassaLite;
  /** La rata da segnare pagata (null se non ce n'e' una aperta). */
  rata: { id: string; importo: number; seq?: number } | null;
  nome: string;
  aperta: boolean;
  /** Nel giorno del calendario: solo icone e menu, niente testo lungo. */
  compatta?: boolean;
}) {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const { pay, remove } = useAdminActions();
  const [pagaOpen, setPagaOpen] = useState(false);
  if (!canWriteAdmin(user)) return null;

  const piano = tassa.rate > 1;
  const inserisci = () => openModal('newTax', { tax: tassa, prefill: { intent: 'cifra', source: 'CONFERMATO' } });
  const menu: ActionItem[] = [
    // Nella scheda c'è già il bottone: nel menu solo dove il bottone non c'è (giorno del calendario). 26/10/2026.
    { key: 'cifra', label: 'Inserisci la cifra del commercialista', icon: 'pencil', hidden: !aperta || piano || !compatta, onClick: inserisci },
    { key: 'modifica', label: 'Modifica la tassa', icon: 'pencil', onClick: () => openModal('newTax', { tax: tassa }) },
    {
      key: 'elimina', label: 'Elimina la tassa', icon: 'trash', danger: true,
      onClick: () => remove.mutate({
        path: `/admin/taxes/${tassa.id}`,
        title: 'Eliminare questa tassa?',
        body: piano ? `${nome}, con tutte le sue ${tassa.rate} rate.` : nome,
        okTitle: 'Tassa eliminata',
      }),
    },
  ];
  const etichettaPaga = piano && rata?.seq ? `Segna pagata la rata ${rata.seq}` : 'Segna pagata';

  return (
    <div className={`deb-azioni${compatta ? ' is-compatta' : ''}`} onClick={(e) => e.stopPropagation()}>
      {aperta && rata && (
        <button className="btn btn-sm" disabled={pay.isPending} onClick={() => setPagaOpen((v) => !v)} title="Per un pagamento che la banca non ha riconosciuto da sola">
          <Icon name="check" size={10} /> {compatta ? 'Segna pagata' : etichettaPaga}
        </button>
      )}
      {aperta && !piano && !compatta && (
        <button className="btn btn-sm btn-ghost" onClick={inserisci} title="Per mettere l'importo vero dell'F24 o del documento">
          <Icon name="pencil" size={10} /> Inserisci la cifra del commercialista
        </button>
      )}
      <ActionsMenu items={menu} label={`Azioni su ${nome}`} />
      {pagaOpen && rata && (
        <SettlePopover
          mode="paga"
          amount={rata.importo}
          target={{ kind: 'TAX_INSTALLMENT', id: rata.id }}
          counterparty={nome}
          side="DEBIT"
          pending={pay.isPending}
          subtitle={piano && rata.seq ? `rata ${rata.seq} di ${tassa.rate}` : null}
          onClose={() => setPagaOpen(false)}
          onConfirm={(v) => { setPagaOpen(false); pay.mutate({ source: 'TAX', id: rata.id, amount: rata.importo, label: nome, ...v }); }}
        />
      )}
    </div>
  );
}

/** Il bottone "Nuova tassa da pagare" delle intestazioni, come in Fisco. */
export function NuovaTassa() {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  if (!canWriteAdmin(user)) return null;
  return (
    <button className="btn btn-primary" onClick={() => openModal('newTax')}>
      <Icon name="plus" /> Nuova tassa da pagare
    </button>
  );
}
